import React from 'react';
import { ArrowRight, CheckCircle, Users, Briefcase, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useParams } from 'react-router-dom';

const expertiseData = {
  'finance': {
    title: 'Comptabilité & Finance',
    subtitle: 'Experts en recrutement financier',
    description: 'Notre équipe spécialisée en Finance & Comptabilité recrute des professionnels qualifiés pour des postes allant du comptable junior au Directeur Financier. Nous comprenons les exigences spécifiques du secteur financier suisse.',
    roles: [
      'Directeur Financier (CFO)',
      'Contrôleur de Gestion',
      'Comptable Senior',
      'Auditeur Interne',
      'Trésorier',
      'Analyste Financier',
      'Responsable Consolidation',
      'Credit Manager'
    ],
    stats: [
      { value: '2,500+', label: 'Placements Finance' },
      { value: '15+', label: 'Consultants spécialisés' },
      { value: '92%', label: 'Taux de rétention' }
    ]
  },
  'technology': {
    title: 'Technologie & Digital',
    subtitle: 'Recrutement IT et Digital',
    description: 'Dans un monde en constante évolution technologique, nous identifions les talents qui maîtrisent les dernières technologies et peuvent accompagner votre transformation digitale.',
    roles: [
      'CTO / Directeur Technique',
      'Développeur Full Stack',
      'Data Scientist',
      'DevOps Engineer',
      'Chef de Projet IT',
      'Architecte Cloud',
      'Cybersecurity Expert',
      'Product Manager'
    ],
    stats: [
      { value: '1,800+', label: 'Placements Tech' },
      { value: '12+', label: 'Consultants spécialisés' },
      { value: '89%', label: 'Taux de rétention' }
    ]
  },
  'services-financiers': {
    title: 'Services Financiers',
    subtitle: 'Banque, Assurance & Asset Management',
    description: 'Notre expertise dans le secteur des services financiers couvre la banque privée, la gestion d\'actifs, l\'assurance et les fintech. Nous comprenons les exigences réglementaires et les compétences spécifiques recherchées.',
    roles: [
      'Relationship Manager',
      'Portfolio Manager',
      'Compliance Officer',
      'Risk Manager',
      'Investment Analyst',
      'Private Banker',
      'Actuaire',
      'Head of Operations'
    ],
    stats: [
      { value: '3,200+', label: 'Placements Banking' },
      { value: '18+', label: 'Consultants spécialisés' },
      { value: '94%', label: 'Taux de rétention' }
    ]
  },
  'juridique': {
    title: 'Juridique & Conformité',
    subtitle: 'Legal, Compliance & Regulatory',
    description: 'Nous recrutons des professionnels du droit et de la conformité pour des cabinets d\'avocats, des entreprises et des institutions financières. Notre réseau inclut des juristes spécialisés dans tous les domaines du droit des affaires.',
    roles: [
      'General Counsel',
      'Legal Counsel',
      'Head of Compliance',
      'Regulatory Affairs Manager',
      'Contract Manager',
      'Data Protection Officer',
      'Paralegal Senior',
      'Corporate Secretary'
    ],
    stats: [
      { value: '1,100+', label: 'Placements Legal' },
      { value: '8+', label: 'Consultants spécialisés' },
      { value: '91%', label: 'Taux de rétention' }
    ]
  },
  'interim': {
    title: 'Management de Transition',
    subtitle: 'Interim Management & Executive Search',
    description: 'Nous mettons à disposition des managers et dirigeants expérimentés pour des missions temporaires. Transformation, restructuration, remplacement ou projet spécifique : nos managers de transition sont opérationnels immédiatement.',
    roles: [
      'CEO de Transition',
      'CFO de Transition',
      'DRH de Transition',
      'Directeur de Projet',
      'Turnaround Manager',
      'Change Manager',
      'PMO',
      'Directeur Industriel'
    ],
    stats: [
      { value: '500+', label: 'Missions réalisées' },
      { value: '48h', label: 'Délai d\'intervention' },
      { value: '98%', label: 'Missions réussies' }
    ]
  }
};

const ExpertisePage = () => {
  const { slug } = useParams();
  const expertise = expertiseData[slug] || expertiseData['finance'];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="bg-[#E30022] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-serif mb-4">{expertise.title}</h1>
          <p className="text-xl opacity-90">{expertise.subtitle}</p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {expertise.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-gray-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-serif text-gray-800 mb-6">Notre expertise</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">{expertise.description}</p>
              
              <a 
                href="/recrutement"
                className="inline-flex items-center gap-2 bg-[#E30022] text-white px-6 py-3 rounded hover:bg-[#c7001d] transition-colors font-medium group"
              >
                Confier un recrutement
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Postes que nous recrutons</h3>
              <div className="grid grid-cols-2 gap-3">
                {expertise.roles.map((role, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#E30022] flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{role}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-serif text-gray-800 mb-4">Vous recherchez un poste dans ce domaine ?</h2>
          <p className="text-gray-600 mb-8">Consultez nos offres d'emploi ou déposez votre CV pour être contacté.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/emplois"
              className="inline-flex items-center gap-2 bg-[#E30022] text-white px-6 py-3 rounded hover:bg-[#c7001d] transition-colors font-medium group"
            >
              Voir les offres
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="/deposer-cv"
              className="inline-flex items-center gap-2 border-2 border-[#E30022] text-[#E30022] px-6 py-3 rounded hover:bg-[#E30022] hover:text-white transition-colors font-medium group"
            >
              Déposer mon CV
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExpertisePage;
